import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Empty, Tag, Select } from "antd";
// REDUX
import { useSelector } from "react-redux";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// INTERFACES
import {
	ModbusToLn2Entry,
	ResponseKind,
	UInt16,
} from "../../../../../interfaces/AdapterCdoModbus.interface";
import { RootState } from "../../../../../app/store";
// CONSTANTS
import { DEFAULT_MODBUS_MSG_TO_LN2 } from "../../../../../constants/adapter";
// COMPONENTS
import FormInputBooleanSwitch from "../../../../components/form/FormInputBooleanSwitch";
import FormInputNumber from "../../../../components/form/FormInputNumber";
import FormSelectResponseMessageResponse from "../../../../components/form/FormSelectResponseMessageResponse";
import FormSelectMultipleBoolean from "../../../../components/form/FormSelectMultipleBoolean";
import FormSelectMultipleNumbers from "../../../../components/form/FormSelectMultipleNumbers";
import ComponentFrontEndMsg from "./ComponentFrontEndMsg";
import ComponentMsg from "./ComponentMsg";

export default function ComponentMsgToLn2({
	msgToLn2Map,
	setMsgToLn2Map,
}: {
	msgToLn2Map: ModbusToLn2Entry[];
	setMsgToLn2Map: (newValue: ModbusToLn2Entry[]) => void;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [msgToLn2, setMsgToLn2] = useState<ModbusToLn2Entry>({
		...DEFAULT_MODBUS_MSG_TO_LN2,
	});
	const [responseType, setResponseType] = useState<"value" | "bit_mask_data">("value");

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeReadCoilsResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "ReadCoilsResponse", data: [] },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeReadDiscreteInputsResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "ReadDiscreteInputsResponse", data: [] },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeReadHoldingRegistersResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "ReadHoldingRegistersResponse", data: [] },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeReadInputRegistersResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "ReadInputRegistersResponse", data: [] },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeReadWriteMultipleRegistersResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "ReadWriteMultipleRegistersResponse", read_data: [] },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeWriteSingleCoilResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "WriteSingleCoilResponse", address: 0, value: false },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeWriteMultipleCoilsResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "WriteMultipleCoilsResponse", address: 0, count: 0 },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeWriteSingleRegisterResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: { kind: "WriteSingleRegisterResponse", address: 0, value: 0 },
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeWriteMultipleRegistersResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: {
					kind: "WriteMultipleRegistersResponse",
					address: 0,
					count: 0,
				},
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeReportServerIdResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: {
					kind: "ReportServerIdResponse",
					server_id: 0,
					running: false,
					additional_data: [],
				},
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeMaskWriteRegisterResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: {
					kind: "MaskWriteRegisterResponse",
					address: 0,
					and_mask: 0,
					or_mask: 0,
				},
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeCustomResponse = () => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				message: {
					kind: "CustomResponse",
					function_code: 0,
					data: [],
				},
				type: prev.key_1.type,
			},
		}));
	};

	const onChangeMsgToLn2kind = (newValue: ResponseKind) => {
		switch (newValue) {
			case "ReadCoilsResponse":
				onChangeReadCoilsResponse();
				break;
			case "ReadDiscreteInputsResponse":
				onChangeReadDiscreteInputsResponse();
				break;
			case "ReadHoldingRegistersResponse":
				onChangeReadHoldingRegistersResponse();
				break;
			case "ReadInputRegistersResponse":
				onChangeReadInputRegistersResponse();
				break;
			case "ReadWriteMultipleRegistersResponse":
				onChangeReadWriteMultipleRegistersResponse();
				break;
			case "WriteSingleCoilResponse":
				onChangeWriteSingleCoilResponse();
				break;
			case "WriteMultipleCoilsResponse":
				onChangeWriteMultipleCoilsResponse();
				break;
			case "WriteSingleRegisterResponse":
				onChangeWriteSingleRegisterResponse();
				break;
			case "WriteMultipleRegistersResponse":
				onChangeWriteMultipleRegistersResponse();
				break;
			case "ReportServerIdResponse":
				onChangeReportServerIdResponse();
				break;
			case "MaskWriteRegisterResponse":
				onChangeMaskWriteRegisterResponse();
				break;
			case "CustomResponse":
				onChangeCustomResponse();
				break;
			default:
				break;
		}
	};

	const onClickAddNewMessage = () => {
		let copy: ModbusToLn2Entry[] = structuredClone(msgToLn2Map);
		copy = [...copy, msgToLn2];
		setMsgToLn2Map(copy);
	};

	const onClickDeleteMessage = (indexToDelete: number) => {
		const copy = msgToLn2Map.filter((_, index) => index !== indexToDelete);
		setMsgToLn2Map(copy);
	};

	const onChangeResponseType = (newValue: "value" | "bit_mask_data") => {
		setResponseType(newValue);
		if (newValue === "value") {
			setMsgToLn2((prev: ModbusToLn2Entry) => ({
				...prev,
				key_1: {
					...prev.key_1,
					type: { value: null },
				},
			}));
		} else {
			setMsgToLn2((prev: ModbusToLn2Entry) => ({
				...prev,
				key_1: {
					...prev.key_1,
					type: { bit_mask_data: 0 },
				},
			}));
		}
	};

	const onChangeBitMaskData = (newValue: number) => {
		setMsgToLn2((prev: ModbusToLn2Entry) => ({
			...prev,
			key_1: {
				...prev.key_1,
				type: { bit_mask_data: newValue },
			},
		}));
	};

	// ************************************************ */
	// USE EFFECT ************************************* */

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='container'>
			{/* *********************************************** */}
			{/* MAP LN2 MESSAGES / DELETE MESSAGE */}
			{msgToLn2Map.length === 0 ? (
				<Empty description='No messages added' />
			) : (
				<>
					{msgToLn2Map.map((item: ModbusToLn2Entry, index: number) => (
						<div className={`drawer__box__${theme} row mt-2`} key={index}>
							<div className='col-md-12 mt-3'>
								<div className='generalStyles__flex'>
									<div className='generalStyles__inputFlexNameLarge'>
										<span className='generalStyles__inputFlexRequired'>*</span>
										LN2 to Message
									</div>
									<div className='generalStyles__width100'>
										<div className='generalStyles__flex'>
											<div className='generalStyles__width100'>
												<Tag
													color='cyan'
													style={{ textAlign: "center", width: "100%" }}
												>
													{item.key_1.message.kind}
												</Tag>
											</div>
											<div>
												<Button
													className='buttonStyle__29'
													shape='circle'
													style={{ marginLeft: "10px", marginTop: "-5px" }}
													icon={
														<FontAwesomeIcon
															icon={faTrash}
															onClick={() => onClickDeleteMessage(index)}
														/>
													}
												/>
											</div>
										</div>
									</div>
									<div className='generalStyles__infoTooltipIconBlank' />
								</div>
							</div>
						</div>
					))}
				</>
			)}
			{/* *********************************************** */}
			{/* RESPONSE MESSAGE  */}
			<div className={`drawer__box__${theme} row mt-3`}>
				<FormSelectResponseMessageResponse
					disabledInput={false}
					inputValue={msgToLn2.key_1.message.kind}
					inputOnChange={(value: ResponseKind) => onChangeMsgToLn2kind(value)}
				/>
				<FormInputNumber
					inputValue={msgToLn2.key_2}
					inputLabelName='key_2'
					inputOnChange={(newValue: number) => {
						setMsgToLn2((prev: ModbusToLn2Entry) => ({
							...prev,
							key_2: newValue,
						}));
					}}
					inputVariableName='key_2'

				/>

				<div className='col-md-12 mt-3'>
					<div className='generalStyles__flex'>
						<div className='generalStyles__inputFlexNameLarge'>
							<span className='generalStyles__inputFlexRequired'>*</span>
							Response Type
						</div>
						<div className='generalStyles__width100'>
							<Select
								value={responseType}
								onChange={onChangeResponseType}
								style={{ width: '100%' }}
								options={[
									{ value: 'value', label: 'Value' },
									{ value: 'bit_mask_data', label: 'Bit Mask Data' },
								]}
							/>
						</div>
						<div className='generalStyles__infoTooltipIconBlank' />
					</div>
				</div>

				{responseType === 'bit_mask_data' && (
					<FormInputNumber
						inputValue={'bit_mask_data' in msgToLn2.key_1.type ? msgToLn2.key_1.type.bit_mask_data : 0}
						inputLabelName='Bit Mask Data'
						inputOnChange={onChangeBitMaskData}
						inputVariableName='bit_mask_data'

					/>
				)}

				{msgToLn2.key_1.message.kind === "ReadCoilsResponse" && (
					<FormSelectMultipleBoolean
						disabledInput={false}
						inputValue={msgToLn2.key_1.message.data}
						inputOnChange={(newValue: boolean[]) => {
							setMsgToLn2((prev: ModbusToLn2Entry) => ({
								...prev,
								key_1: {
									message: {
										kind: "ReadCoilsResponse",
										data: newValue,
									},
									type: prev.key_1.type,
								},
							}));
						}}
					/>
				)}

				{msgToLn2.key_1.message.kind === "ReadDiscreteInputsResponse" && (
					<FormSelectMultipleBoolean
						disabledInput={false}
						inputValue={msgToLn2.key_1.message.data}
						inputOnChange={(newValue: boolean[]) => {
							setMsgToLn2((prev: ModbusToLn2Entry) => ({
								...prev,
								key_1: {
									message: {
										kind: "ReadDiscreteInputsResponse",
										data: newValue,
									},
									type: prev.key_1.type,
								},
							}));
						}}
					/>
				)}

				{msgToLn2.key_1.message.kind === "ReadHoldingRegistersResponse" && (
					<FormSelectMultipleNumbers
						disabledInput={false}
						inputValue={msgToLn2.key_1.message.data}
						inputOnChange={(newValue: UInt16[]) => {
							setMsgToLn2((prev: ModbusToLn2Entry) => ({
								...prev,
								key_1: {
									message: {
										kind: "ReadHoldingRegistersResponse",
										data: newValue,
									},
									type: prev.key_1.type,
								},
							}));
						}}
					/>
				)}

				{msgToLn2.key_1.message.kind === "ReadInputRegistersResponse" && (
					<FormSelectMultipleNumbers
						disabledInput={false}
						inputValue={msgToLn2.key_1.message.data}
						inputOnChange={(newValue: UInt16[]) => {
							setMsgToLn2((prev: ModbusToLn2Entry) => ({
								...prev,
								key_1: {
									message: {
										kind: "ReadInputRegistersResponse",
										data: newValue,
									},
									type: prev.key_1.type,
								},
							}));
						}}
					/>
				)}

				{msgToLn2.key_1.message.kind ===
					"ReadWriteMultipleRegistersResponse" && (
						<FormSelectMultipleNumbers
							disabledInput={false}
							inputValue={msgToLn2.key_1.message.read_data}
							inputOnChange={(newValue: UInt16[]) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "ReadWriteMultipleRegistersResponse",
											read_data: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
						/>
					)}

				{msgToLn2.key_1.message.kind === "WriteSingleCoilResponse" && (
					<>
						<FormInputNumber
							inputValue={msgToLn2.key_1.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteSingleCoilResponse",
											address: newValue,
											value:
												prev.key_1.message.kind === "WriteSingleCoilResponse"
													? prev.key_1.message.value
													: false,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='address'

						/>
						<FormInputBooleanSwitch
							value={msgToLn2.key_1.message.value}
							onChangeSwitch={(newValue: boolean) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteSingleCoilResponse",
											address:
												prev.key_1.message.kind === "WriteSingleCoilResponse"
													? prev.key_1.message.address
													: 0,
											value: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							labelName='Value'
							variableName='value'
							isRequired
							disabledInput={false}

						/>
					</>
				)}

				{msgToLn2.key_1.message.kind === "WriteMultipleCoilsResponse" && (
					<>
						<FormInputNumber
							inputValue={msgToLn2.key_1.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteMultipleCoilsResponse",
											address: newValue,
											count:
												prev.key_1.message.kind === "WriteMultipleCoilsResponse"
													? prev.key_1.message.count
													: 0,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='address'

						/>

						<FormInputNumber
							inputValue={msgToLn2.key_1.message.count}
							inputLabelName='count'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteMultipleCoilsResponse",
											address:
												prev.key_1.message.kind === "WriteMultipleCoilsResponse"
													? prev.key_1.message.address
													: 0,
											count: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='count'

						/>
					</>
				)}

				{msgToLn2.key_1.message.kind === "WriteMultipleRegistersResponse" && (
					<>
						<FormInputNumber
							inputValue={msgToLn2.key_1.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteMultipleRegistersResponse",
											address: newValue,
											count:
												prev.key_1.message.kind ===
													"WriteMultipleRegistersResponse"
													? prev.key_1.message.count
													: 0,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='address'

						/>

						<FormInputNumber
							inputValue={msgToLn2.key_1.message.count}
							inputLabelName='count'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteMultipleRegistersResponse",
											address:
												prev.key_1.message.kind ===
													"WriteMultipleRegistersResponse"
													? prev.key_1.message.address
													: 0,
											count: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='count'

						/>
					</>
				)}

				{msgToLn2.key_1.message.kind === "WriteSingleRegisterResponse" && (
					<>
						<FormInputNumber
							inputValue={msgToLn2.key_1.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteSingleRegisterResponse",
											address: newValue,
											value:
												prev.key_1.message.kind ===
													"WriteSingleRegisterResponse"
													? prev.key_1.message.value
													: 0,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='address'

						/>

						<FormInputNumber
							inputValue={msgToLn2.key_1.message.value}
							inputLabelName='value'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "WriteSingleRegisterResponse",
											address:
												prev.key_1.message.kind ===
													"WriteSingleRegisterResponse"
													? prev.key_1.message.address
													: 0,
											value: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='value'

						/>
					</>
				)}

				{msgToLn2.key_1.message.kind === "ReportServerIdResponse" && (
					<>
						<FormInputNumber
							inputValue={msgToLn2.key_1.message.server_id}
							inputLabelName='server_id'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "ReportServerIdResponse",
											server_id: newValue,
											running:
												prev.key_1.message.kind === "ReportServerIdResponse"
													? prev.key_1.message.running
													: false,
											additional_data:
												prev.key_1.message.kind === "ReportServerIdResponse"
													? prev.key_1.message.additional_data
													: [],
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='server_id'

						/>

						<FormInputBooleanSwitch
							value={msgToLn2.key_1.message.running}
							onChangeSwitch={(newValue: boolean) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "ReportServerIdResponse",
											server_id:
												prev.key_1.message.kind === "ReportServerIdResponse"
													? prev.key_1.message.server_id
													: 0,
											running: newValue,
											additional_data:
												prev.key_1.message.kind === "ReportServerIdResponse"
													? prev.key_1.message.additional_data
													: [],
										},
										type: prev.key_1.type,
									},
								}));
							}}
							labelName='running'
							variableName='running'
							isRequired
							disabledInput={false}

						/>

						<FormSelectMultipleNumbers
							disabledInput={false}
							inputValue={msgToLn2.key_1.message.additional_data}
							inputOnChange={(newValue: UInt16[]) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "ReportServerIdResponse",
											server_id:
												prev.key_1.message.kind === "ReportServerIdResponse"
													? prev.key_1.message.server_id
													: 0,
											running:
												prev.key_1.message.kind === "ReportServerIdResponse"
													? prev.key_1.message.running
													: false,
											additional_data: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
						/>
					</>
				)}

				{msgToLn2.key_1.message.kind === "MaskWriteRegisterResponse" && (
					<>
						<FormInputNumber
							inputValue={msgToLn2.key_1.message.address}
							inputLabelName='address'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "MaskWriteRegisterResponse",
											address: newValue,
											and_mask:
												prev.key_1.message.kind === "MaskWriteRegisterResponse"
													? prev.key_1.message.and_mask
													: 0,
											or_mask:
												prev.key_1.message.kind === "MaskWriteRegisterResponse"
													? prev.key_1.message.or_mask
													: 0,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='address'

						/>

						<FormInputNumber
							inputValue={msgToLn2.key_1.message.and_mask}
							inputLabelName='and_mask'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "MaskWriteRegisterResponse",
											address:
												prev.key_1.message.kind === "MaskWriteRegisterResponse"
													? prev.key_1.message.address
													: 0,
											and_mask: newValue,
											or_mask:
												prev.key_1.message.kind === "MaskWriteRegisterResponse"
													? prev.key_1.message.or_mask
													: 0,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='and_mask'

						/>

						<FormInputNumber
							inputValue={msgToLn2.key_1.message.or_mask}
							inputLabelName='or_mask'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "MaskWriteRegisterResponse",
											address:
												prev.key_1.message.kind === "MaskWriteRegisterResponse"
													? prev.key_1.message.address
													: 0,
											and_mask:
												prev.key_1.message.kind === "MaskWriteRegisterResponse"
													? prev.key_1.message.and_mask
													: 0,
											or_mask: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='or_mask'

						/>
					</>
				)}

				{msgToLn2.key_1.message.kind === "CustomResponse" && (
					<>
						<FormInputNumber
							inputValue={msgToLn2.key_1.message.function_code}
							inputLabelName='function_code'
							inputOnChange={(newValue: number) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "CustomResponse",
											function_code: newValue,
											data:
												prev.key_1.message.kind === "CustomResponse"
													? prev.key_1.message.data
													: [],
										},
										type: prev.key_1.type,
									},
								}));
							}}
							inputVariableName='function_code'

						/>

						<FormSelectMultipleNumbers
							disabledInput={false}
							inputValue={msgToLn2.key_1.message.data}
							inputOnChange={(newValue: UInt16[]) => {
								setMsgToLn2((prev: ModbusToLn2Entry) => ({
									...prev,
									key_1: {
										message: {
											kind: "CustomResponse",
											function_code:
												prev.key_1.message.kind === "CustomResponse"
													? prev.key_1.message.function_code
													: 0,
											data: newValue,
										},
										type: prev.key_1.type,
									},
								}));
							}}
						/>
					</>
				)}
			</div>

			{/* *********************************************** */}
			{/* FRONTEND MESSAGE  */}
			<div className={`drawer__box__${theme} row mt-3`}>
				<ComponentFrontEndMsg
					value={msgToLn2.val}
					onChange={(newValue) => {
						setMsgToLn2((prev: ModbusToLn2Entry) => ({
							...prev,
							val: newValue,
						}));
					}}
					disabledInput={false}
				/>
				<ComponentMsg
					value={msgToLn2.val}
					onChange={(newValue) => {
						setMsgToLn2((prev: ModbusToLn2Entry) => ({
							...prev,
							val: newValue,
						}));
					}}
					disabledInput={false}
				/>
			</div>

			{/* *********************************************** */}
			{/* ADD BUTTON  */}
			<div className='col-md-12 mt-3'>
				<div className='generalStyles__flex'>
					<div className='generalStyles__inputFlexNameLarge'>
						<span className='generalStyles__inputFlexRequired' />
					</div>
					<div className='generalStyles__width100'>
						<Button
							onClick={onClickAddNewMessage}
							className='buttonStyle__28 generalStyles__width100'
						>
							<FontAwesomeIcon icon={faPlus} className='generalStyles__mrFix' />
							{t("general.add")}
						</Button>
					</div>
					<div className='generalStyles__infoTooltipIconBlank' />
				</div>
			</div>
		</div>
	);
}
